// ── Module registry ──────────────────────────────────────────────────────────
// Single source of truth for the admin console's top-level MODULES. Each module
// contributes nav sections and routes. `buildMenuList()` yields the exact shape
// SideBar/Menu already render; `buildRoutes()` yields {path, element} for the
// router in jsx/index.js. Adding a screen = one entry here (no edits elsewhere).
//
// A second module (e.g. "Connect" — a UI for praxxis-connect) drops in later as
// another entry in MODULES with its own basePath namespace.

import React, { lazy } from 'react';
import { SECTION_COLORS, SECTION_COLOR_RGB } from '../data/themeTokens';

// ── Praxxis Admin pages (lazy for code-splitting) ────────────────────────────
const Distributors        = lazy(() => import('./praxxis-admin/pages/Distributors'));
const Contracts           = lazy(() => import('./praxxis-admin/pages/Contracts'));
const RebatePrograms      = lazy(() => import('./praxxis-admin/pages/RebatePrograms'));
const NdcHcpcs            = lazy(() => import('./praxxis-admin/pages/NdcHcpcs'));
const TenantDistributors  = lazy(() => import('./praxxis-admin/pages/TenantDistributors'));
const TenantContracts     = lazy(() => import('./praxxis-admin/pages/TenantContracts'));
const ReportTables        = lazy(() => import('./praxxis-admin/pages/ReportTables'));
const ReportColumns       = lazy(() => import('./praxxis-admin/pages/ReportColumns'));
const ReportRelationships = lazy(() => import('./praxxis-admin/pages/ReportRelationships'));
const Tenants             = lazy(() => import('./praxxis-admin/pages/Tenants'));
const ContractDetail      = lazy(() => import('./praxxis-admin/pages/ContractDetail'));
const RebateProgramDetail = lazy(() => import('./praxxis-admin/pages/RebateProgramDetail'));
const GlobalInventory     = lazy(() => import('./praxxis-admin/pages/GlobalInventory'));
const HistoricalRebateData = lazy(() => import('./praxxis-admin/pages/HistoricalRebateData'));
const TenantUsers         = lazy(() => import('./praxxis-admin/pages/TenantUsers'));
const TenantContractAssignments = lazy(() => import('./praxxis-admin/pages/TenantContractAssignments'));

// ── Connect (praxxis-connect ODBC integration monitoring) pages ──────────────
const ConnectDashboard    = lazy(() => import('./praxxis-connect/pages/ConnectDashboard'));
const OdbcClients         = lazy(() => import('./praxxis-connect/pages/OdbcClients'));
const ConnectSegments     = lazy(() => import('./praxxis-connect/pages/ConnectSegments'));
const ConnectRuns         = lazy(() => import('./praxxis-connect/pages/ConnectRuns'));

export const MODULES = [
  {
    key: 'praxxis-admin',
    title: 'Praxxis Admin User Layer (PAUL)',
    basePath: 'admin',
    icon: <i className="las la-shield-alt"></i>,
    color: SECTION_COLORS.settings,
    colorRgb: SECTION_COLOR_RGB.settings,
    sections: [
      {
        title: 'Global Purchasing',
        icon: <i className="las la-truck"></i>,
        color: SECTION_COLORS.purchasing,
        colorRgb: SECTION_COLOR_RGB.purchasing,
        children: [
          { title: 'Distributors',        to: 'admin/distributors',        component: Distributors },
          { title: 'Contracts',           to: 'admin/contracts',           component: Contracts },
          { title: 'Contract Detail',     to: 'admin/contracts/:id',       component: ContractDetail, hidden: true },
          { title: 'Rebate Programs',     to: 'admin/rebate-programs',     component: RebatePrograms },
          { title: 'Rebate Detail',       to: 'admin/rebate-programs/:id', component: RebateProgramDetail, hidden: true },
          { title: 'NDC → HCPCS',         to: 'admin/ndc-hcpcs',           component: NdcHcpcs },
          { title: 'Client Distributors', to: 'admin/tenant-distributors', component: TenantDistributors },
          { title: 'Client Contracts',    to: 'admin/tenant-contracts',    component: TenantContracts },
          { title: 'Historical Rebate Data', to: 'admin/historical-rebate-data', component: HistoricalRebateData },
        ],
      },
      {
        title: 'Report Schema Manager',
        icon: <i className="las la-database"></i>,
        color: SECTION_COLORS.reports,
        colorRgb: SECTION_COLOR_RGB.reports,
        children: [
          { title: 'Tables',        to: 'admin/report-tables',        component: ReportTables },
          { title: 'Columns',       to: 'admin/report-columns',       component: ReportColumns },
          { title: 'Relationships', to: 'admin/report-relationships', component: ReportRelationships },
        ],
      },
      {
        title: 'Tenants',
        icon: <i className="las la-building"></i>,
        color: SECTION_COLORS.management,
        colorRgb: SECTION_COLOR_RGB.management,
        children: [
          { title: 'Tenant Registry', to: 'admin/tenants', component: Tenants },
          { title: 'Tenant Users', to: 'admin/tenant-users', component: TenantUsers },
          { title: 'Tenant Contracts', to: 'admin/tenant-contract-assignments', component: TenantContractAssignments },
        ],
      },
      {
        title: 'Inventory',
        icon: <i className="las la-boxes"></i>,
        color: SECTION_COLORS.inventory,
        colorRgb: SECTION_COLOR_RGB.inventory,
        children: [
          { title: 'Global Inventory', to: 'admin/global-inventory', component: GlobalInventory },
        ],
      },
    ],
  },

  {
    key: 'praxxis-connect',
    title: 'Connect',
    basePath: 'connect',
    icon: <i className="las la-plug"></i>,
    color: SECTION_COLORS.connect,
    colorRgb: SECTION_COLOR_RGB.connect,
    sections: [
      {
        title: 'ODBC Integration',
        icon: <i className="las la-plug"></i>,
        color: SECTION_COLORS.connect,
        colorRgb: SECTION_COLOR_RGB.connect,
        children: [
          { title: 'Overview',     to: 'connect',          component: ConnectDashboard },
          { title: 'ODBC Clients', to: 'connect/clients',  component: OdbcClients },
          { title: 'Segments',     to: 'connect/segments', component: ConnectSegments },
          { title: 'Run History',  to: 'connect/runs',     component: ConnectRuns },
        ],
      },
    ],
  },
];

// Build the MenuList array SideBar consumes: one entry per section.
export function buildMenuList(modules = MODULES) {
  const list = [];
  for (const mod of modules) {
    for (const section of mod.sections) {
      list.push({
        title: section.title,
        color: section.color || mod.color,
        colorRgb: section.colorRgb || mod.colorRgb,
        classsChange: 'mm-collapse',
        iconStyle: section.icon || mod.icon,
        content: section.children.filter(c => !c.hidden).map(c => ({ title: c.title, to: c.to })),
      });
    }
  }
  return list;
}

// Build the flat route table: {path, element} where element is the lazy component.
export function buildRoutes(modules = MODULES) {
  const routes = [];
  for (const mod of modules) {
    for (const section of mod.sections) {
      for (const child of section.children) {
        routes.push({ path: child.to, element: child.component });
      }
    }
  }
  return routes;
}
